/**
 * Builds the query string for /at-risk-monitor/at-risk-moves.
 *
 * Extracted from AtRiskMonitor so it can double as a request *signature*. The
 * page writes its filter state in stages while booting — permissions apply a
 * default region, saved global filters restore, the cascade prunes reps and
 * customers — and each write re-runs the fetch effect. Those stages land
 * seconds apart, so the in-flight abort never catches them and the page issued
 * the same 237 kB request four times on load.
 *
 * Comparing this string to the last one fetched makes those redundant passes
 * cheap to detect: same filters in, same string out.
 *
 * Values are sorted so that selecting HOUSTON then DALLAS produces the same
 * signature as DALLAS then HOUSTON — without that the comparison would miss
 * most real duplicates.
 */
/** Which day the board is scoped to. Today is the live board (today's
 *  appointments plus past-due and missed). Tomorrow shows ONLY moves whose
 *  appointment or deadline falls on tomorrow's date, for night dispatchers
 *  planning past midnight. */
export type AtRiskDayScope = 'today' | 'tomorrow';

export interface AtRiskMovesFilters {
  orderDispatchers: string[];
  driverDispatchers: string[];
  regions: string[];
  csrs: string[];
  salesReps: string[];
  customers: string[];
  /** Omitted (or 'today') sends nothing, so every pre-existing signature is
   *  unchanged. 'tomorrow' rides in the string so flipping the selector is a
   *  real refetch rather than a dropped repeat. */
  day?: AtRiskDayScope;
  /** Include mule drivers by default; false explicitly requests road drivers. */
  showMules?: boolean;
  /** Orders-scope registry fieldnames added as columns. Part of the request, so it
   *  has to be part of the signature — otherwise adding a column produces the same
   *  string as before and the fetch is dropped as a redundant repeat. */
  additionalFields?: string[];
}

export function buildAtRiskMovesParams(filters: AtRiskMovesFilters): string {
  const params = new URLSearchParams();

  const add = (key: string, values: string[]) => {
    if (values && values.length > 0) {
      params.append(key, [...values].sort().join(','));
    }
  };

  // Order is fixed (not dependent on caller) so the string is comparable.
  add('dispatcher', filters.orderDispatchers);
  add('driver_dispatcher', filters.driverDispatchers);
  add('region', filters.regions);
  add('csr', filters.csrs);
  add('salesrep', filters.salesReps);
  add('customer', filters.customers);

  if (filters.day === 'tomorrow') {
    params.append('day', 'tomorrow');
  }
  if (filters.showMules === false) {
    params.append('show_mules', 'false');
  }

  // NOT sorted, unlike the filters above: the backend keeps the user's selection
  // order (it decides which row-expanding branch wins), so reordering here would
  // send a different query than the one the picker describes.
  if (filters.additionalFields && filters.additionalFields.length > 0) {
    params.append('additional_fields', filters.additionalFields.join(','));
  }

  return params.toString();
}
